/**
 * DeliveryDetailTab Component
 * 배달 상세 탭 (기존 배달이력 페이지)
 */
import React, { useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatsCard } from './shared/StatsCard';
import { FeeCard } from './shared/FeeCard';
import { SlotsCard } from './shared/SlotsCard';
import { useDeliveryHistory } from '../hooks/useDeliveryHistory';
import { useDeferredLoading } from '../../../../core/hooks/useDeferredLoading';
import { getUserInfo } from '../../../../core/utils';
import styles from '../styles/DeliveryDetailTab.module.css';

export const DeliveryDetailTab: React.FC = () => {
    const navigate = useNavigate();
    const fromDateRef = useRef<HTMLInputElement>(null);
    const toDateRef = useRef<HTMLInputElement>(null);

    const isSupplier = useMemo(() => {
        const userInfo = getUserInfo();
        return !!userInfo?.suppId;
    }, []);

    const {
        deliveryStats,
        fromDate,
        toDate,
        preAcctYn,
        handleFromDateChange,
        handleToDateChange,
        isLoading: rawIsLoading,
    } = useDeliveryHistory();

    const isLoading = useDeferredLoading(rawIsLoading, 200);

    const formatDateToYyyymmdd = (dateString: string): string => {
        if (!dateString) return '';
        return dateString.replace(/-/g, '');
    };

    const handleSlotClick = (slotType: string) => {
        if (isSupplier || !fromDate || !toDate) {
            return;
        }

        const params = new URLSearchParams();
        params.set('fromDate', formatDateToYyyymmdd(fromDate));
        params.set('toDate', formatDateToYyyymmdd(toDate));
        params.set('slotType', slotType);

        navigate(`/delivery-history/detail?${params.toString()}`);
    };

    const isWeeklySettlement = () => {
        return preAcctYn === 'N';
    };

    const handleFromDateIconClick = () => {
        if (fromDateRef.current) {
            fromDateRef.current.focus();
            fromDateRef.current.showPicker?.();
        }
    };

    const handleToDateIconClick = () => {
        if (toDateRef.current) {
            toDateRef.current.focus();
            toDateRef.current.showPicker?.();
        }
    };

    const handleFromDateChangeWithRestriction = (value: string) => {
        if (isWeeklySettlement()) {
            const selectedDate = new Date(value);
            const dayOfWeek = selectedDate.getDay();
            if (dayOfWeek !== 3) {
                return;
            }
        }
        handleFromDateChange(value);
    };

    const getWednesdayOnlyDateRange = () => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();

        const sixMonthsAgo = new Date(currentYear, currentMonth - 6, 1);
        const firstWednesday = new Date(sixMonthsAgo);
        const dayOfWeek = sixMonthsAgo.getDay();
        const daysToAdd = dayOfWeek <= 3 ? (3 - dayOfWeek) : (10 - dayOfWeek);
        firstWednesday.setDate(sixMonthsAgo.getDate() + daysToAdd);

        const sixMonthsLater = new Date(currentYear, currentMonth + 6, 1);
        const lastWednesday = new Date(sixMonthsLater);
        const lastDayOfWeek = sixMonthsLater.getDay();
        const daysToSubtract = lastDayOfWeek >= 3 ? (lastDayOfWeek - 3) : (lastDayOfWeek + 4);
        lastWednesday.setDate(sixMonthsLater.getDate() - daysToSubtract);

        return {
            min: firstWednesday.toISOString().split('T')[0],
            max: lastWednesday.toISOString().split('T')[0]
        };
    };

    return (
        <>
            {/* 필터 섹션 */}
            <div className={styles['filter-section']}>
                <div className={styles['date-range-wrapper']}>
                    <div className={styles['date-input-box']}>
                        <input
                            ref={fromDateRef}
                            type="date"
                            className={styles['date-input']}
                            value={fromDate}
                            onChange={(e) => isWeeklySettlement() ? handleFromDateChangeWithRestriction(e.target.value) : handleFromDateChange(e.target.value)}
                            onKeyDown={(e) => e.preventDefault()}
                            min={isWeeklySettlement() ? getWednesdayOnlyDateRange().min : undefined}
                            max={isWeeklySettlement() ? getWednesdayOnlyDateRange().max : undefined}
                            title={isWeeklySettlement() ? "주정산 기간에는 수요일만 선택 가능합니다" : undefined}
                        />
                        <i className={`fas fa-calendar-alt ${styles['date-icon']}`} onClick={handleFromDateIconClick}></i>
                    </div>
                    <span className={styles['date-separator']}>~</span>
                    <div className={styles['date-input-box']}>
                        <input
                            ref={toDateRef}
                            type="date"
                            className={`${styles['date-input']} ${isWeeklySettlement() ? styles['disabled'] : ''}`}
                            value={toDate}
                            onChange={(e) => handleToDateChange(e.target.value)}
                            onKeyDown={(e) => e.preventDefault()}
                            disabled={isWeeklySettlement()}
                        />
                        <i
                            className={`fas fa-calendar-alt ${styles['date-icon']} ${isWeeklySettlement() ? styles['disabled'] : ''}`}
                            onClick={isWeeklySettlement() ? undefined : handleToDateIconClick}
                        ></i>
                    </div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['detail-container']}>
                {isLoading ? (
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>배달 이력을 불러오는 중...</p>
                    </div>
                ) : (
                    <>
                        <StatsCard
                            completedCount={deliveryStats.completedCount}
                            rejectedCount={deliveryStats.rejectedCount}
                            acceptanceRate={deliveryStats.acceptanceRate}
                        />
                        
                        <FeeCard deliveryFee={deliveryStats.deliveryFee} />
                        
                        <SlotsCard
                            morningLunchPeakCount={deliveryStats.morningLunchPeakCount}
                            afternoonNonPeakCount={deliveryStats.afternoonNonPeakCount}
                            eveningPeakCount={deliveryStats.eveningPeakCount}
                            nightPeakCount={deliveryStats.nightPeakCount}
                            onSlotClick={handleSlotClick}
                            clickable={!isSupplier}
                        />
                    </>
                )}
            </div>
        </>
    );
};
